import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRegistryPerms } from '../lib/perms'
import { Plus, Trash2, Pencil, ShieldAlert } from 'lucide-react'
import ChecklistWizard from './ChecklistWizard'
import { useConfirm, deleteJSON } from './ConfirmDialog'
import { ErrorState } from './ViewState'
import { useWS } from '../contexts/WSContext'
import { SEVERITIES, checklistLang, checklistCatName, checklistItemTitle } from '../equipment'

const sevColor = (v) => SEVERITIES.find(s => s.value === v)?.color || 'text-slate-400'

// Administração › Checklists: lista + wizard de cadastro/edição.
export default function AdminChecklists() {
  const { t, i18n } = useTranslation()
  const { mayEdit, mayDelete, reasonEdit, reasonDelete } = useRegistryPerms()
  const { checklists, models, adminError, reloadAdmin } = useWS()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [confirmDialog, ask] = useConfirm()
  const lang = checklistLang(i18n.language)

  const openCreate = () => { setEditing(null); setOpen(true) }
  const openEdit = (c) => { setEditing(c); setOpen(true) }
  const usedBy = (c) => models.filter(m => m.checklist_id === c.id)

  // O documento pode estar vinculado a modelos de veículo em operação, então o diálogo
  // diz quantos e o erro do servidor aparece nele.
  const remove = (c) => ask({
    title: t('admin:checklists.delete_title'),
    message: t('admin:checklists.delete_message', { name: c.name, models: usedBy(c).length }),
    confirmLabel: t('common:actions.delete'),
    danger: true,
    onConfirm: async () => { await deleteJSON(`/api/checklists/${c.id}`); reloadAdmin() },
  })

  return (
    <div className="h-full overflow-y-auto p-6 scrollbar-thin">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('admin:checklists.count', { count: checklists.length })}</p>
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreate} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-brand text-white hover:bg-brand-700 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={16} /> {t('admin:checklists.add')}
        </button>
      </div>

      {adminError ? (
        <ErrorState message={adminError} onRetry={reloadAdmin} />
      ) : checklists.length === 0 ? (
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreate} className="w-full flex flex-col items-center justify-center gap-2 h-48 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:border-brand/50 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={28} />
          <span className="text-sm">{t('admin:checklists.empty_cta')}</span>
        </button>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {checklists.map(c => {
            const items = Array.isArray(c.items) ? c.items : []
            const cats = Array.isArray(c.categories) ? c.categories : []
            const blockers = items.filter(it => it.blocker).length
            const uncategorized = items.filter(it => !it.category || !cats.some(k => k.code === it.category)).length
            const models_ = usedBy(c)
            return (
              <div key={c.id} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm truncate">{c.name}</p>
                  <div className="flex items-center gap-1 shrink-0">
                    <span title={t('admin:checklists.version_title')}
                      className="rounded-md bg-brand/10 text-brand-700 dark:text-brand-400 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums">
                      v{c.version || 1}
                    </span>
                    <button disabled={!mayEdit} onClick={() => openEdit(c)} title={!mayEdit ? t(reasonEdit) : t('common:actions.edit')} className="text-slate-300 dark:text-slate-600 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"><Pencil size={15} /></button>
                    <button disabled={!mayDelete} onClick={() => remove(c)} title={!mayDelete ? t(reasonDelete) : t('admin:actions.remove')} className="text-slate-300 dark:text-slate-600 hover:text-status-down disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={15} /></button>
                  </div>
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {t('admin:checklists.items_count', { count: items.length })}
                  {blockers > 0 && (
                    <> · <ShieldAlert size={11} className="inline -mt-0.5 text-status-down" /> {t('admin:checklists.blockers_count', { count: blockers })}</>
                  )}
                  {models_.length > 0 && <> · {t('admin:checklists.used_by', { models: models_.map(m => m.name).join(', ') })}</>}
                </p>

                {(cats.length > 0 || uncategorized > 0) && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {cats.map(k => (
                      <span key={k.code} className="inline-flex items-center gap-1 rounded-md bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[11px] text-slate-600 dark:text-slate-300">
                        {checklistCatName(k, lang)}
                        <span className="tabular-nums text-slate-400 dark:text-slate-500">{items.filter(it => it.category === k.code).length}</span>
                      </span>
                    ))}
                    {uncategorized > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[11px] text-slate-400 dark:text-slate-500">
                        {t('admin:checklists.no_category')}
                        <span className="tabular-nums">{uncategorized}</span>
                      </span>
                    )}
                  </div>
                )}

                {items.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {items.slice(0, 4).map((it, i) => (
                      <li key={i} className="text-xs text-slate-600 dark:text-slate-300 truncate">
                        <span className={`font-semibold ${sevColor(it.severity)}`}>{it.severity}</span> · {checklistItemTitle(it, lang)}
                      </li>
                    ))}
                    {items.length > 4 && <li className="text-[11px] text-slate-400 dark:text-slate-500">{t('admin:checklists.more_items', { count: items.length - 4 })}</li>}
                  </ul>
                )}
              </div>
            )
          })}
        </div>
      )}

      <ChecklistWizard key={editing?.id ?? 'new'} open={open} onClose={() => setOpen(false)} editing={editing} />
      {confirmDialog}
    </div>
  )
}
